
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Fonction pour obtenir l'IP réelle de l'utilisateur
const fetchRealIP = async (): Promise<string | null> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Failed to fetch real IP:', error);
    return null;
  }
};

export const useSecurityAudit = () => {
  const logSecurityEvent = useMutation({
    mutationFn: async ({ 
      eventType, 
      eventData, 
      ipAddress, 
      userAgent 
    }: {
      eventType: string;
      eventData?: Record<string, any>;
      ipAddress?: string;
      userAgent?: string;
    }) => {
      try {
        // Récupérer les informations utilisateur actuelles
        const { data: { user } } = await supabase.auth.getUser();
        
        // Récupérer le profil complet si utilisateur connecté
        let userProfile = null;
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, email, role')
            .eq('id', user.id)
            .single();
          userProfile = profile;
        }

        // Récupérer l'IP réelle si non fournie
        const realIP = ipAddress || await fetchRealIP();
        
        // Enrichir les données de l'événement
        const enrichedEventData = {
          ...eventData,
          timestamp: new Date().toISOString(),
          browser: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screen: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(userProfile && {
            username: userProfile.username,
            email: userProfile.email,
            role: userProfile.role
          })
        };

        // Use the new database function for persistent logging
        const { error } = await supabase.rpc('log_security_event', {
          p_event_type: eventType,
          p_event_data: enrichedEventData,
          p_ip_address: realIP,
          p_user_agent: userAgent || navigator.userAgent
        });

        if (error) {
          console.error('Failed to log security event to database:', error);
        }

        return { success: true };
      } catch (error) {
        console.error('Security audit error:', error);
        return { success: false, error };
      }
    },
    onError: (error) => {
      console.error('Failed to log security event:', error);
    }
  });

  return { logSecurityEvent: logSecurityEvent.mutate };
};
